import * as React from 'react';
import styled from 'styled-components';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import { CardHeader } from '../../shared/card/CardHeader';
import { Brand } from '../../shared/Brand';
import { Column } from '../../layout/Column';
import { LinearLoading } from '../../shared/loading/LinearLoading';

/**
 * SAML Callback Page
 *
 * This page receives the redirect from the SAML bridge after authentication.
 * It extracts the token from the URL and sends it back to the parent window
 * via postMessage (for popup flow) or handles it directly (for redirect flow).
 */
export const SAMLCallback = (): React.ReactElement => {
  const [message, setMessage] = React.useState<string>('Processing authentication...');
  const [isError, setIsError] = React.useState<boolean>(false);

  React.useEffect(() => {
    try {
      // Extract token or error from URL query parameters
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const error = urlParams.get('error');

      if (window.opener) {
        // Popup flow: Send result to parent window
        window.opener.postMessage(
          {
            type: 'saml_auth',
            token: token || undefined,
            error: error || undefined,
          },
          window.location.origin
        );

        // Close popup after a short delay
        setTimeout(() => {
          window.close();
        }, 500);

        setMessage('Authentication successful. Closing window...');
      } else {
        // Redirect flow fallback: Display error message
        // (In practice, the parent page should always open this in a popup)
        if (error) {
          setMessage(`Authentication failed: ${error}`);
          setIsError(true);
        } else if (token) {
          setMessage('Authentication successful. You can close this window.');
        } else {
          setMessage('No authentication data received.');
          setIsError(true);
        }
      }
    } catch (err) {
      const error = err as { code?: string; message?: string };
      console.error('SAML callback error:', error.code || 'unknown');
      setMessage('An unexpected error occurred during authentication.');
      setIsError(true);
    }
  }, []);

  return (
    <Container>
      <StyledCard>
        <Column>
          <CardHeader surface={8} style={{ justifyContent: 'center' }}>
            <Brand role='presentation' />
          </CardHeader>
          {!isError && <LinearLoading />}
          <CardBody
            notScrollable
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              padding: '2rem',
            }}
          >
            <Message isError={isError}>{message}</Message>
          </CardBody>
        </Column>
      </StyledCard>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 991px) {
    max-width: 340px;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 600px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);

  @media (max-width: 991px) {
    max-width: 340px;
  }
`;

const Message = styled.p<{ isError: boolean }>`
  font-size: 1rem;
  text-align: center;
  color: ${(props) => (props.isError ? '#e53e3e' : '#2d3748')};
  margin: 0;
`;
